import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

/**
 * FilterDropdown — dropdown dimensional multiselect reusable (GH#24).
 *
 * Extraído 1:1 de los 5 renderers de UnifiedFilterPanel (marca/sitio/categoría/
 * subcategoría/segmento), que eran idénticos salvo label/estado/opciones. Es la
 * ÚNICA implementación del dropdown de filtro; la consumen tanto UnifiedFilterPanel
 * (Publicaciones) como FilterBox (Data Health / futuros listados).
 *
 * Self-contained: maneja su propio estado de open + búsqueda + click-outside (via ref).
 * Al abrirse dispara `onOpen` (para el auto-load de opciones que hacía el panel viejo).
 * El patrón pending-removal se preserva: destildar una opción APLICADA no la saca de
 * `applied` directo — la agrega a `pendingRemove` para que el botón Filtrar se reactive.
 */

export interface FilterDropdownMeta {
  counts: Record<string, number>;
  total: number;
}

export interface FilterDropdownLabels {
  /** Placeholder cuando no hay selección, ej. "Todas las marcas". */
  all: string;
  /** Sufijo singular, ej. "marca seleccionada" (se prefija con "1 "). */
  singular: string;
  /** Sufijo plural, ej. "marcas seleccionadas" (se prefija con el conteo). */
  plural: string;
  searchPlaceholder: string;
  /** Texto cuando options está vacío (solo si showNoOptions). */
  noOptions?: string;
  /** Etiqueta del select-all concordada por género, ej. "Todas" / "Todos" (PT-#45 #10). */
  selectAll: string;
}

export interface FilterDropdownProps {
  label: string;
  /** Ícono a la izquierda del label (PT-#45 #16). Opcional; se atenúa igual que el label. */
  icon?: React.ReactNode;
  options: string[];
  meta?: FilterDropdownMeta | null;
  /** Selección pendiente (aún no aplicada). */
  filter: string[];
  /** Selección ya aplicada (viaja al fetch). */
  applied: string[];
  /** Aplicadas marcadas para quitar pero sin re-aplicar todavía. */
  pendingRemove?: string[];
  setFilter: (value: string[]) => void;
  setPendingRemove?: (value: string[]) => void;
  /**
   * Quita una opción YA APLICADA de inmediato (destildar = misma acción que la X de la
   * pastilla). Si se provee, tiene prioridad sobre `setPendingRemove` (que quedaba en
   * strikethrough hasta Filtrar). Ver bug "deselección deja pill tachada".
   */
  onRemoveApplied?: (value: string) => void;
  labels: FilterDropdownLabels;
  /** Mostrar "Sin opciones" cuando options está vacío (subcategoría/segmento). */
  showNoOptions?: boolean;
  /** Se dispara al abrir el dropdown (auto-load de opciones). */
  onOpen?: () => void;
  widthStyle?: React.CSSProperties;
  /**
   * Recalculo de counts en curso (faceted refresh). Estándar Algolia/Baymard/NN-g:
   * NO se vacían opciones — se mantienen los counts previos ATENUADOS
   * (stale-while-revalidate) + spinner inline junto al label, y los controles quedan
   * interactivos. Ver docs/pendientes / issue PT-#47.
   */
  loading?: boolean;
}

// Formatea contador con thousands separator es-AR.
const fmtN = (n: number) => n.toLocaleString('es-AR');

// Pone '(vacío)' SIEMPRE al inicio; el resto mantiene el orden del backend.
const emptyFirst = (a: string, b: string) => {
  if (a === '(vacío)') return -1;
  if (b === '(vacío)') return 1;
  return 0;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  icon,
  options,
  meta = null,
  filter,
  applied,
  pendingRemove = [],
  setFilter,
  setPendingRemove,
  onRemoveApplied,
  labels,
  showNoOptions = false,
  onOpen,
  widthStyle,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // Click-outside self-contained (via ref, sin depender de clases únicas).
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onOpen) onOpen();
  };

  const selectedCount = filter.length + applied.length;

  const handleToggleOption = (value: string, checked: boolean) => {
    if (checked) {
      if (!filter.includes(value) && !applied.includes(value)) {
        setFilter([...filter, value]);
      }
    } else {
      // Destildar una APLICADA = quitarla de inmediato (misma acción que la X de la
      // pastilla). Si el host provee onRemoveApplied lo usamos; si no, fallback legacy
      // al patrón pendingRemove (strikethrough hasta Filtrar).
      if (applied.includes(value)) {
        if (onRemoveApplied) onRemoveApplied(value);
        else if (setPendingRemove) setPendingRemove([...(pendingRemove || []), value]);
      }
      if (filter.includes(value)) {
        setFilter(filter.filter(v => v !== value));
      }
    }
  };

  // Selección efectiva de una opción (misma regla que el render): (filter ∪ applied) − pendingRemove.
  const isSelectedOf = (opt: string) =>
    (filter.includes(opt) || applied.includes(opt)) && !pendingRemove.includes(opt);

  // Opciones visibles (tras search + orden) y las que son clickeables (no dead-end zero-count).
  const visibleOptions = options
    .filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort(emptyFirst);
  const clickableOptions = visibleOptions.filter(
    opt => !(!loading && !isSelectedOf(opt) && !!meta && meta.counts[opt] === 0),
  );
  const allVisibleSelected = clickableOptions.length > 0 && clickableOptions.every(isSelectedOf);
  const someVisibleSelected = clickableOptions.some(isSelectedOf);

  // PT-#45 #10 — "TODOS": select/deselect all de las opciones VISIBLES clickeables (respeta el
  // search activo). Batch en un solo setState; para las ya aplicadas usa pendingRemove (misma
  // semántica reversible que la X de pastilla, se confirma con Filtrar).
  const toggleAllVisible = () => {
    const inClickable = new Set(clickableOptions);
    if (allVisibleSelected) {
      // Deseleccionar todas las visibles.
      setFilter(filter.filter(v => !inClickable.has(v)));
      const appliedToRemove = clickableOptions.filter(v => applied.includes(v) && !pendingRemove.includes(v));
      if (appliedToRemove.length > 0 && setPendingRemove) {
        setPendingRemove([...pendingRemove, ...appliedToRemove]);
      }
    } else {
      // Seleccionar todas las visibles no seleccionadas.
      const toAdd = clickableOptions.filter(v => !filter.includes(v) && !applied.includes(v));
      if (toAdd.length > 0) setFilter([...filter, ...toAdd]);
      // Re-incluir aplicadas que estaban marcadas para quitar.
      const restored = pendingRemove.filter(v => inClickable.has(v));
      if (restored.length > 0 && setPendingRemove) {
        setPendingRemove(pendingRemove.filter(v => !inClickable.has(v)));
      }
    }
  };

  // Dim de counts durante el refresh (stale-while-revalidate): se atenúan los números,
  // NO las opciones ni los checkboxes (siguen interactivos y a color pleno).
  const countDimClass = loading ? 'opacity-40 transition-opacity' : 'transition-opacity';

  return (
    <div ref={rootRef} style={widthStyle} aria-busy={loading}>
      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
        {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
        <span>{label}</span>
        {loading && (
          <span className="flex items-center gap-1 text-xs font-normal italic text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            <span role="status" aria-live="polite">Actualizando</span>
          </span>
        )}
      </label>
      <div className="relative">
        <div
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between hover:border-gray-400"
          onClick={toggle}
        >
          <span className="text-sm text-gray-700 tabular-nums">
            {selectedCount > 0
              ? selectedCount === 1
                ? `1 ${labels.singular}`
                : `${selectedCount} ${labels.plural}`
              : meta
                ? <>{labels.all} <span className={countDimClass}>({fmtN(meta.total)})</span></>
                : labels.all}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>

        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder={labels.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="max-h-40 overflow-y-auto">
              {/* PT-#45 #10 — "TODOS": tildar/destildar todas las opciones visibles (Excel-style). */}
              {clickableOptions.length > 0 && (
                <label className="flex items-center px-3 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleAllVisible}
                    className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">{labels.selectAll}</span>
                </label>
              )}
              {visibleOptions
                .map((opt) => {
                  const isSelected = (filter.includes(opt) || applied.includes(opt)) && !pendingRemove.includes(opt);
                  // Grey-out (Baymard): opción con count 0 tras filtrar y NO seleccionada →
                  // atenuada + no-clickeable (dead-end). Nunca se desactiva lo ya elegido.
                  // Solo se juzga con counts frescos (no durante loading, para no bloquear
                  // por un valor viejo que va a cambiar).
                  const isZeroCount = !loading && !isSelected && !!meta && meta.counts[opt] === 0;
                  return (
                    <label
                      key={opt}
                      className={`flex items-center px-3 py-2 ${isZeroCount ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isZeroCount}
                        onChange={(e) => handleToggleOption(opt, e.target.checked)}
                        className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span className={`text-sm flex-1 ${isZeroCount ? 'text-gray-400' : opt === '(vacío)' ? 'text-gray-400 italic' : 'text-gray-700'}`}>{opt}</span>
                      {meta && meta.counts[opt] != null && (
                        <span className={`text-xs text-gray-500 tabular-nums ml-2 ${countDimClass}`}>{fmtN(meta.counts[opt])}</span>
                      )}
                    </label>
                  );
                })}
              {showNoOptions && options.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-500">{labels.noOptions}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
